<section class="mb-4 rounded-lg border border-gray-200 bg-white p-4">
  <!-- Header -->
  <div class="mb-6 flex items-center justify-between">
    <h2 class="text-lg text-primary-500">Pack Assignment</h2>
    <p-button
      label="Modify pack options"
      icon="pi pi-pencil"
      (click)="showDialog()"
    />
  </div>

  <!-- Selected Pack -->
  <app-display-pack-options
    [pack]="pack"
    [usedTrips]="usedTrips"
    [usedUsers]="usedUsers"
    [displayCTA]="false"
  />
</section>

<!-- dialog -->
<p-dialog
  header="Update pack options"
  [modal]="true"
  [(visible)]="visible"
  [formGroup]="packForm"
  (onHide)="closeDialog()"
>
  <section class="flex flex-col gap-4">
    <div>
      <label for="packSelection" class="text-primary-500">
        Selected Pack
      </label>
      <p-select
        id="packSelection"
        formControlName="id"
        [options]="packOptions"
        optionValue="id"
        optionLabel="name"
        placeholder="Select a Pack"
        [fluid]="true"
      />
      @if (!company().size) {
        <div
          class="mt-2 rounded-lg border border-error-200 bg-error-50 px-4 py-2 text-paragraph-s-reg text-error-950"
        >
          Select a company size to be able to modify the pack
        </div>
      }
    </div>
    <div>
      <h2 class="mb-2 text-base text-primary-500">Usage Limits</h2>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <!-- Number of Trips -->
        <div>
          <label for="tripLimit" class="text-sm text-primary-500"
            >Trip limit (default: {{ defaultPackOptions?.tripLimit }})</label
          >
          <p-inputNumber
            id="tripLimit"
            formControlName="tripLimit"
            [min]="0"
            mode="decimal"
            [fluid]="true"
          />
        </div>
        <!-- Max Trips -->

        <!-- Logins -->
        <div>
          <label for="userLimit" class="text-sm text-primary-500"
            >Users (default: {{ defaultPackOptions?.userLimit }})</label
          >
          <p-inputNumber
            id="userLimit"
            formControlName="userLimit"
            [min]="0"
            mode="decimal"
            [fluid]="true"
          />
        </div>
        <!-- Max Logins -->
        <div>
          <label for="extraUserLimit" class="text-sm text-primary-500"
            >Extra purchasable users (default:
            {{
              defaultPackOptions?.extraUserLimit !== null
                ? defaultPackOptions?.extraUserLimit
                : "unlimited"
            }})</label
          >
          <p-inputNumber
            id="extraUserLimit"
            formControlName="extraUserLimit"
            [min]="1"
            mode="decimal"
            [fluid]="true"
            [placeholder]="'unlimited'"
          />
        </div>
      </div>
    </div>
    <div>
      <h2 class="mb-2 text-base text-primary-500">Features</h2>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <!-- Export PDF -->
        <div class="flex items-center gap-2">
          <p-checkbox
            formControlName="exportPDF"
            inputId="exportPDF"
            [binary]="true"
          />
          <label for="exportPDF" class="text-sm text-primary-500"
            >Export PDF (default:
            <i
              class="pi text-lg"
              [ngClass]="
                defaultPackOptions?.exportPDF ? 'pi-check' : 'pi-times'
              "
            ></i
            >)</label
          >
        </div>
        <!-- Export Excel -->
        <div class="flex items-center gap-2">
          <p-checkbox
            formControlName="exportExcel"
            inputId="exportExcel"
            [binary]="true"
          />
          <label for="exportExcel" class="text-sm text-primary-500"
            >Export Excel (default:
            <i
              class="pi text-lg"
              [ngClass]="
                defaultPackOptions?.exportExcel ? 'pi-check' : 'pi-times'
              "
            ></i
            >)</label
          >
        </div>
        <!-- Client Filter -->
        <div class="flex items-center gap-2">
          <p-checkbox
            formControlName="clientFilter"
            inputId="clientFilter"
            [binary]="true"
          />
          <label for="clientFilter" class="text-sm text-primary-500"
            >Client Filter (default:
            <i
              class="pi text-lg"
              [ngClass]="
                defaultPackOptions?.clientFilter ? 'pi-check' : 'pi-times'
              "
            ></i
            >)</label
          >
        </div>
        <!-- Bulk Upload (max files per batch) -->
        <div>
          <label for="bulkUpload" class="text-sm text-primary-500"
            >Max files per bulk upload (default:
            {{ defaultPackOptions?.bulkUpload ?? 0 }})</label
          >
          <p-inputNumber
            id="bulkUpload"
            formControlName="bulkUpload"
            [min]="0"
            mode="decimal"
            [fluid]="true"
          />
        </div>
        <!-- Comparison Graph -->
        <div class="flex items-center gap-2">
          <p-checkbox
            formControlName="comparisonGraph"
            inputId="comparisonGraph"
            [binary]="true"
          />
          <label for="comparisonGraph" class="text-sm text-primary-500"
            >Comparison Graph (default:
            <i
              class="pi text-lg"
              [ngClass]="
                defaultPackOptions?.comparisonGraph ? 'pi-check' : 'pi-times'
              "
            ></i
            >)</label
          >
        </div>

        <!-- Use API -->
        <div class="flex items-center gap-2">
          <p-checkbox
            formControlName="useApi"
            inputId="useApi"
            [binary]="true"
          />
          <label for="useApi" class="text-sm text-primary-500"
            >Use API (default:
            <i
              class="pi text-lg"
              [ngClass]="defaultPackOptions?.useApi ? 'pi-check' : 'pi-times'"
            ></i
            >)</label
          >
        </div>

        <!-- Use Google API (requires Use API) -->
        <div class="flex items-center gap-2">
          <p-checkbox
            formControlName="useGoogleApi"
            inputId="useGoogleApi"
            [binary]="true"
          />
          <label
            for="useGoogleApi"
            class="text-sm"
            [class.text-primary-500]="!packForm.controls.useGoogleApi.disabled"
            [class.text-gray-400]="packForm.controls.useGoogleApi.disabled"
            >Use Google API (default:
            <i
              class="pi text-lg"
              [ngClass]="
                defaultPackOptions?.useGoogleApi ? 'pi-check' : 'pi-times'
              "
            ></i
            >)</label
          >
        </div>

        <!-- API calls per month -->
        <div>
          <label for="apiCallsPerMonth" class="text-sm text-primary-500"
            >API calls / month (default:
            {{
              defaultPackOptions?.apiCallsPerMonth
                ? defaultPackOptions?.apiCallsPerMonth
                : "unlimited"
            }})</label
          >
          <p-inputNumber
            id="apiCallsPerMonth"
            formControlName="apiCallsPerMonth"
            [min]="0"
            mode="decimal"
            [fluid]="true"
            [placeholder]="'unlimited'"
          />
        </div>
      </div>
    </div>
  </section>
  <ng-template #footer>
    <section class="flex flex-row justify-between gap-4">
      <p-button label="cancel" severity="secondary" (onClick)="closeDialog()" />
      <p-button
        label="save"
        icon="pi pi-check"
        [loading]="loading"
        (onClick)="submit()"
        [disabled]="packForm.invalid"
      />
    </section>
  </ng-template>
</p-dialog>
